import { FC, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Typography } from "antd"
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"

import { useStore } from "stores/utils/use-store"
import { SubmitCancelButtons } from "components"
import { RestockConfig, RestockWindow } from "domains/marketplace/store/types"
import { getTimezones } from "./timezones"

const TIME_FORMAT_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

export const RestockWindows: FC<{ onClose: () => void }> = observer(({ onClose }) => {
  const { dataStore: { marketplaceStore } } = useStore()

  const { id: itemId, name: itemName } = marketplaceStore.selectedElement
  const [form] = Form.useForm()
  const [stock, setStock] = useState<RestockConfig["stock"]>(null)
  const [loaded, setLoaded] = useState<boolean>(false)
  const timezones = useMemo(getTimezones, [])

  useEffect(() => {
    let active = true
    if (itemId) {
      marketplaceStore.getRestockConfig(itemId).then(config => {
        // A failed load resolves null — bail so a save cannot wipe the stored windows.
        if (!active || !config || !Array.isArray(config.windows)) return
        setStock(config.stock)
        form.setFieldsValue({ windows: config.windows })
        setLoaded(true)
      })
    }

    return () => {
      active = false
    }
  }, [itemId, form, marketplaceStore])

  const save = async(windows: RestockWindow[]) => {
    marketplaceStore.funcAfterSuccess = async() => {
      onClose()
      await marketplaceStore.getData()
    }
    const saved = await marketplaceStore.updateRestockWindows(itemId, windows)
    if (!saved) marketplaceStore.resetFuncAfterSuccess()
  }

  const onFinish = async(values: { windows?: RestockWindow[] }) => {
    const windows = (values.windows || []).map(w => ({
      timezone: w.timezone,
      startLocal: w.startLocal,
      endLocal: w.endLocal,
      unitsPerDay: Number(w.unitsPerDay),
      restocksPerDay: Number(w.restocksPerDay),
      enabled: w.enabled ?? true,
    }))
    if (!windows.length) {
      Modal.confirm({
        title: "Remove all restock windows?",
        content: "This reverts the item to the legacy stock logic and drops its restock pool.",
        okText: "Remove",
        okType: "danger",
        onOk: () => save(windows),
      })

      return
    }
    await save(windows)
  }

  return (
    <>
      <Typography.Title level={5}>Restock windows — {itemName}</Typography.Title>
      {stock && (
        <Alert
          type={stock.disabledLowStock ? "error" : "info"}
          showIcon
          style={{ marginBottom: "12px" }}
          message={`Pool: ${stock.availableUnits} available · ${
            stock.isOutOfStock ? "out of stock" : "in stock"
          }${stock.disabledLowStock ? " · disabled (low inventory)" : ""}`}
        />
      )}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: "16px" }}
        message={
          "Adding windows opts this item into pooled restocking: it becomes out of stock until the next "
          + "restock event releases units. Saving an empty list reverts it to the legacy stock logic."
        }
      />
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.List name="windows">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "12px 16px", marginBottom: 12 }}
                >
                  <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                    <Typography.Text strong>Window {name + 1}</Typography.Text>
                    <Button
                      type="text"
                      danger
                      size="small"
                      aria-label={`Remove window ${name + 1}`}
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                    />
                  </Space>
                  <Form.Item
                    {...restField}
                    name={[name, "timezone"]}
                    label="Timezone (IANA)"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Select
                      showSearch
                      placeholder="e.g. America/Los_Angeles"
                      options={timezones.map(tz => ({ label: tz, value: tz }))}
                    />
                  </Form.Item>
                  <Space>
                    <Form.Item
                      {...restField}
                      name={[name, "startLocal"]}
                      label="Start (HH:mm)"
                      rules={[{ required: true, pattern: TIME_FORMAT_REGEX, message: "HH:mm (24h)" }]}
                    >
                      <Input placeholder="08:00" style={{ width: "120px" }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "endLocal"]}
                      label="End (HH:mm)"
                      rules={[{ required: true, pattern: TIME_FORMAT_REGEX, message: "HH:mm (24h)" }]}
                    >
                      <Input placeholder="12:00" style={{ width: "120px" }} />
                    </Form.Item>
                  </Space>
                  <Space>
                    <Form.Item
                      {...restField}
                      name={[name, "unitsPerDay"]}
                      label="Units / day"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber min={0} precision={0} style={{ width: "120px" }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "restocksPerDay"]}
                      label="Restocks / day"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber min={1} precision={0} style={{ width: "120px" }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "enabled"]}
                      label="Enabled"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Space>
                </div>
              ))}
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() => add({ restocksPerDay: 1, enabled: true })}
                style={{ marginBottom: "12px" }}
              >
                Add window
              </Button>
            </>
          )}
        </Form.List>
        <SubmitCancelButtons onClose={onClose} isLoading={!loaded || marketplaceStore.isLoading} />
      </Form>
    </>
  )
})
